import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { useAccountPasswordValidation } from '../../hooks/useAccountPasswordValidation';
import { UserRole } from '../../types/firestore';
import {
  Alert,
  Button,
  FormControl,
  FormLabel,
  Input,
  Radio,
  Spinner,
} from '../common/UIComponents';
import {
  ArrowRightIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  LockClosedIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import PasswordInput from '../common/PasswordInput';
import PasswordPolicyChecklist from '../common/PasswordPolicyChecklist';
import { savePostAuthIntentFromQueryValue } from '../../utils/postAuthIntent';

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  displayName?: string;
}

interface RegisterFormProps {
  selectedRole: UserRole;
  onRoleChange?: (role: UserRole) => void;
  showRoleSelection?: boolean;
}

const RegisterForm: React.FC<RegisterFormProps> = ({
  selectedRole,
  onRoleChange,
  showRoleSelection = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterFormData>();

  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from;
  const queryParams = new URLSearchParams(location.search);
  const postAuthIntent = queryParams.get('postAuthIntent');

  useEffect(() => {
    if (!postAuthIntent) {
      return;
    }
    savePostAuthIntentFromQueryValue(postAuthIntent, 'register-form-query');
  }, [postAuthIntent]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailAlreadyInUse, setEmailAlreadyInUse] = useState<string | null>(null);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const password = watch('password') || '';
  const confirmPassword = watch('confirmPassword') || '';
  const passwordValidation = useAccountPasswordValidation(password);
  const canSubmitRegistration =
    Boolean(executeRecaptcha) &&
    !loading &&
    Boolean(password) &&
    passwordValidation.isValid &&
    password === confirmPassword;

  const handleRegistrationSubmit = useCallback(
    async (data: RegisterFormData) => {
      if (!executeRecaptcha) {
        setError('Błąd inicjalizacji reCAPTCHA. Spróbuj odświeżyć stronę.');
        return;
      }

      setLoading(true);
      setError(null);
      setEmailAlreadyInUse(null);

      if (!passwordValidation.isValid) {
        setLoading(false);
        setError('Hasło nie spełnia wymagań bezpieczeństwa.');
        return;
      }

      try {
        await executeRecaptcha('register');
        const registerResult = await authService.register(
          data.email,
          data.password,
          data.displayName || data.email.split('@')[0],
          selectedRole
        );

        const verifyTarget = postAuthIntent
          ? `/verify-email?postAuthIntent=${postAuthIntent}`
          : '/verify-email';
        navigate(verifyTarget, {
          state: {
            email: data.email,
            initialVerificationEmail: registerResult.verificationEmail,
            registeredRole: selectedRole,
            from,
          },
        });
      } catch (err: any) {
        if (err.code === 'auth/email-already-in-use') {
          const normalizedEmail = String(data.email || '').trim();
          setEmailAlreadyInUse(normalizedEmail || null);
          setError('To konto już istnieje. Zaloguj się tym adresem i dokończ weryfikację email.');
        } else if (err.code && typeof authService.mapFirebaseError === 'function') {
          setError(authService.mapFirebaseError(err.code));
        } else {
          setError(
            'Wystąpił nieoczekiwany błąd podczas rejestracji. Sprawdź konsolę lub spróbuj ponownie.'
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [executeRecaptcha, navigate, passwordValidation.isValid, selectedRole, from, postAuthIntent]
  );

  const roleCardBase =
    'p-4 rounded-lg border transition-colors duration-150 ease-in-out cursor-pointer';
  const roleCardInactive =
    'border-primary-dark-300/48 bg-primary-dark-100/28 hover:bg-primary-dark-300/30';
  const roleCardClientActive = 'border-ratuj-300/70 bg-ratuj-300/10';
  const roleCardServiceActive = 'border-ratuj-300/70 bg-ratuj-300/10';

  return (
    <form onSubmit={handleSubmit(handleRegistrationSubmit)} className="space-y-6 animate-fade-in">
      {error && (
        <Alert
          variant="error"
          icon={<ExclamationCircleIcon className="w-5 h-5" />}
          className="mb-6"
        >
          <div className="space-y-3">
            <p>{error}</p>
            {emailAlreadyInUse && (
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => {
                  const loginTarget = postAuthIntent
                    ? `/login?postAuthIntent=${postAuthIntent}`
                    : '/login';
                  return navigate(loginTarget, {
                    state: {
                      emailPrefill: emailAlreadyInUse,
                      onboardingHint:
                        'Konto już istnieje. Zaloguj się i dokończ weryfikację email.',
                      from,
                    },
                  });
                }}
              >
                Przejdź do logowania
              </Button>
            )}
          </div>
        </Alert>
      )}

      {showRoleSelection && onRoleChange && (
        <FormControl>
          <FormLabel className="mb-3 text-base font-medium">Rejestruję się jako:</FormLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              className={`
                ${roleCardBase}
                ${selectedRole === UserRole.CLIENT ? roleCardClientActive : roleCardInactive}
              `}
              onClick={() => onRoleChange(UserRole.CLIENT)}
            >
              <Radio
                id="roleClient"
                value={UserRole.CLIENT}
                label="Klient"
                checked={selectedRole === UserRole.CLIENT}
                onChange={() => onRoleChange(UserRole.CLIENT)}
                className="absolute opacity-0 w-0 h-0"
              />
              <label htmlFor="roleClient" className="flex items-start cursor-pointer space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  <UserIcon
                    className={`w-6 h-6 transition-colors ${selectedRole === UserRole.CLIENT ? 'text-ratuj-300' : 'text-primary-light-400'}`}
                  />
                </div>
                <div>
                  <h3
                    className={`font-semibold text-lg transition-colors ${selectedRole === UserRole.CLIENT ? 'text-ratuj-300' : 'text-primary-light-200'}`}
                  >
                    Klient
                  </h3>
                  <p className="text-sm text-primary-light-400">
                    Szukam serwisu do naprawy sprzętu
                  </p>
                </div>
              </label>
            </div>

            <div
              className={`
                ${roleCardBase}
                ${selectedRole === UserRole.SERVICE_ADMIN ? roleCardServiceActive : roleCardInactive}
              `}
              onClick={() => onRoleChange(UserRole.SERVICE_ADMIN)}
            >
              <Radio
                id="roleServiceAdmin"
                value={UserRole.SERVICE_ADMIN}
                label="Serwis Naprawczy"
                checked={selectedRole === UserRole.SERVICE_ADMIN}
                onChange={() => onRoleChange(UserRole.SERVICE_ADMIN)}
                className="absolute opacity-0 w-0 h-0"
              />
              <label
                htmlFor="roleServiceAdmin"
                className="flex items-start cursor-pointer space-x-3"
              >
                <div className="flex-shrink-0 mt-0.5">
                  <BuildingOfficeIcon
                    className={`w-6 h-6 transition-colors ${selectedRole === UserRole.SERVICE_ADMIN ? 'text-ratuj-300' : 'text-primary-light-400'}`}
                  />
                </div>
                <div>
                  <h3
                    className={`font-semibold text-lg transition-colors ${selectedRole === UserRole.SERVICE_ADMIN ? 'text-ratuj-300' : 'text-primary-light-200'}`}
                  >
                    Serwis Naprawczy
                  </h3>
                  <p className="text-sm text-primary-light-400">Oferuję usługi naprawy sprzętu</p>
                </div>
              </label>
            </div>
          </div>
        </FormControl>
      )}

      <Input
        id="displayName"
        label={
          selectedRole === UserRole.SERVICE_ADMIN
            ? 'Nazwa firmy'
            : 'Nazwa wyświetlana (opcjonalnie)'
        }
        placeholder={
          selectedRole === UserRole.SERVICE_ADMIN ? 'Serwis XYZ Sp. z o.o.' : 'Jan Kowalski'
        }
        icon={<UserIcon className="w-5 h-5 text-primary-light-400/80" />}
        required={selectedRole === UserRole.SERVICE_ADMIN}
        error={errors.displayName?.message}
        className="py-2.5"
        {...register('displayName', {
          required: selectedRole === UserRole.SERVICE_ADMIN ? 'Nazwa firmy jest wymagana' : false,
        })}
      />

      <Input
        id="email"
        type="email"
        label="Adres email"
        placeholder="twoj@email.pl"
        icon={<EnvelopeIcon className="w-5 h-5 text-primary-light-400/80" />}
        required
        error={errors.email?.message}
        className="py-2.5"
        autoComplete="email"
        {...register('email', {
          required: 'Email jest wymagany',
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Nieprawidłowy format adresu email',
          },
        })}
      />

      <PasswordInput
        id="password"
        label="Hasło"
        placeholder="Minimum 8 znaków"
        icon={<LockClosedIcon className="w-5 h-5 text-primary-light-400/80" />}
        required
        error={errors.password?.message}
        autoComplete="new-password"
        className="py-2.5"
        {...register('password', {
          required: 'Hasło jest wymagane',
          minLength: {
            value: 8,
            message: 'Hasło musi mieć co najmniej 8 znaków',
          },
          validate: () => passwordValidation.isValid || 'Hasło nie spełnia wymagań bezpieczeństwa',
        })}
      />

      <PasswordPolicyChecklist password={password} validation={passwordValidation} />

      <PasswordInput
        id="confirmPassword"
        label="Potwierdź hasło"
        placeholder="••••••••"
        icon={<LockClosedIcon className="w-5 h-5 text-primary-light-400/80" />}
        required
        error={errors.confirmPassword?.message}
        autoComplete="new-password"
        className="py-2.5"
        {...register('confirmPassword', {
          required: 'Potwierdzenie hasła jest wymagane',
          validate: (value) => value === password || 'Hasła nie są zgodne',
        })}
      />

      <Button
        type="submit"
        variant="primary"
        fullWidth
        className="py-2.5 text-base font-medium mt-2"
        disabled={!canSubmitRegistration}
      >
        {loading ? (
          <>
            <Spinner size="sm" color="white" className="mr-2" />
            Rejestrowanie...
          </>
        ) : (
          <>
            <span className="inline-block mr-1">Zarejestruj się</span>
            <ArrowRightIcon className="ml-1 inline-block h-5 w-5" />
          </>
        )}
      </Button>

      {!executeRecaptcha && (
        <p style={{ fontSize: '0.8em', color: 'orange', marginTop: '5px' }}>
          Inicjalizowanie zabezpieczeń...
        </p>
      )}

      <p className="text-xs text-primary-light-400/70 mt-4 text-center">
        Ta strona jest chroniona przez reCAPTCHA i obowiązują ją
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-light-300 hover:text-primary-light-200 underline mx-1"
        >
          Polityka Prywatności
        </a>
        oraz
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-light-300 hover:text-primary-light-200 underline ml-1"
        >
          Warunki Korzystania z Usługi
        </a>
        Google.
      </p>
    </form>
  );
};

export default RegisterForm;
